import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Trash2, UserPlus, RefreshCw, FlaskConical, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  member: 'Membre',
  cooper: "COOPER'S",
  expert: 'Expert',
};
const ROLE_COLORS = {
  member: 'bg-blue-100 text-blue-700 border-blue-300',
  cooper: 'bg-purple-100 text-purple-700 border-purple-300',
  expert: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

export default function AdminTestAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', role: 'member', nom: '', prenom: '',
  });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/test-accounts`);
      setAccounts(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.nom || !form.prenom) {
      toast.warning('Tous les champs sont obligatoires');
      return;
    }
    if (form.password.length < 6) {
      toast.warning('Mot de passe : 6 caractères minimum');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/test-accounts`, form);
      toast.success(`Compte test ${ROLE_LABELS[form.role]} créé : ${data.email}`);
      setForm({ email: '', password: '', role: 'member', nom: '', prenom: '' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/api/admin/test-accounts/${toDelete.id}`);
      toast.success(`Compte test supprimé : ${toDelete.email}`);
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Suppression refusée');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="admin-test-accounts-page">
      {/* Bouton retour vers le dashboard Super Admin */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/super-dashboard')}
        className="mb-4 text-gray-600 hover:text-gray-900"
        data-testid="back-to-super-admin-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour au dashboard Super Admin
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <FlaskConical className="h-7 w-7 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold">Comptes de test</h1>
          <p className="text-sm text-gray-500">
            Création rapide de comptes <span className="font-semibold">Membre</span>,{' '}
            <span className="font-semibold">COOPER'S</span> ou{' '}
            <span className="font-semibold">Expert</span> pour la qualification.
            Les comptes sont marqués <code className="text-xs bg-gray-100 px-1 rounded">is_test_account</code> et n'apparaissent pas dans les vrais flux clients.
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className="mb-6 border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="h-5 w-5 text-orange-500" />
            Créer un nouveau compte test
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="t-email">Email</Label>
              <Input id="t-email" type="email" value={form.email}
                     onChange={(e) => update('email', e.target.value)}
                     placeholder="test.demo@example.com"
                     data-testid="test-account-email-input" required />
            </div>
            <div>
              <Label htmlFor="t-pwd">Mot de passe (≥ 6 car.)</Label>
              <Input id="t-pwd" type="text" value={form.password}
                     onChange={(e) => update('password', e.target.value)}
                     placeholder="TestPass123"
                     data-testid="test-account-password-input" required />
            </div>
            <div>
              <Label htmlFor="t-role">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => update('role', v)}>
                <SelectTrigger id="t-role" data-testid="test-account-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Membre</SelectItem>
                  <SelectItem value="cooper">COOPER'S</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="t-prenom">Prénom</Label>
              <Input id="t-prenom" value={form.prenom}
                     onChange={(e) => update('prenom', e.target.value)}
                     data-testid="test-account-prenom-input" required />
            </div>
            <div>
              <Label htmlFor="t-nom">Nom</Label>
              <Input id="t-nom" value={form.nom}
                     onChange={(e) => update('nom', e.target.value)}
                     data-testid="test-account-nom-input" required />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={submitting}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="test-account-submit-btn">
                {submitting ? 'Création…' : 'Créer le compte test'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            Comptes test existants ({accounts.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  data-testid="test-accounts-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {loading ? 'Chargement…' : 'Aucun compte test enregistré.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Identité</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a.id} data-testid={`test-account-row-${a.id}`}>
                      <TableCell>
                        <Badge variant="outline" className={ROLE_COLORS[a.role] || ''}>
                          {ROLE_LABELS[a.role] || a.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{a.email}</TableCell>
                      <TableCell>{a.prenom} {a.nom}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="destructive" size="sm"
                                onClick={() => setToDelete(a)}
                                data-testid={`test-account-delete-btn-${a.id}`}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm delete dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce compte test ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte <strong>{toDelete?.email}</strong> ({ROLE_LABELS[toDelete?.role]})
              et son profil satellite seront supprimés définitivement.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="test-account-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}
                               className="bg-red-600 hover:bg-red-700"
                               data-testid="test-account-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
